import { createReducer, on } from '@ngrx/store';
import {
    SettingEntityAttributes,
    TwoFactorMethodEnum,
    UserAdditionalData,
    UserEntity,
} from '../../entities/user.entity';
import { UsersActionTypes } from '../actions/users.actions';
import { CommissionActionTypes } from '../actions/commission.actions';
import { getLatestUpdatedAt } from '../../helper/reducer.helper';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { CommissionEntity } from '../../entities/commission.entity';
import { CommissionState } from './commission.reducer';
import { FilterApps } from '../../../services/default-filter.service';
import { BaseActionTypes } from '../actions/base.actions';

export const usersFeatureKey = 'users';

export interface UsersState extends EntityState<UserEntity> {
    user: UserEntity;
    manageUserList: UserEntity[];
    fetched: boolean;
    latestUpdatedAt: Date | null;
    settings: SettingEntityAttributes | null;
    settingsFetched: boolean;
}
const adapter = createEntityAdapter<UserEntity>({
    selectId: Entity => Entity.Id,
});
export const initialUsersState: UsersState =  adapter.getInitialState({
    user: null,
    manageUserList: null,
    fetched: false,
    latestUpdatedAt: null,
    settings: null,
    settingsFetched: false,
});

export const usersReducer = createReducer(
    initialUsersState,
    on(UsersActionTypes.UpdateAll, (state, { Payload, updateLatestUpdatedAt }) =>
        adapter.setAll(Payload, { ...state, fetched: true, latestUpdatedAt: updateLatestUpdatedAt ? getLatestUpdatedAt(Payload, state.latestUpdatedAt) : state.latestUpdatedAt }),
    ),
    on(UsersActionTypes.UpdateMany, (state, { Payload, updateLatestUpdatedAt }) =>
        Payload.reduce((s, entity) => adapter.setOne(entity, { ...s }), {
            ...state,
            latestUpdatedAt: updateLatestUpdatedAt ? getLatestUpdatedAt(Payload, state.latestUpdatedAt) : state.latestUpdatedAt,
        }),
    ),
    on(UsersActionTypes.UpdateUser, (state, { Payload }) => {
        return {
            ...state,
            user: Payload,
        }
    }),
    on(UsersActionTypes.UpdateUserSettings, (state, { Payload }) => {
        const settings = Payload ? {...Payload} : Payload;
        if (settings?.ListFilters) {
            settings.ListFilters = new Map(Object.entries(settings.ListFilters)) as Map<FilterApps, object>;
        }
        return {
            ...state,
            settings,
            settingsFetched: true,
        }
    }),
    on(UsersActionTypes.UpdateManageUserList, (state, { Payload }) => ({
        ...state,
        manageUserList: Payload,
    })),
    on(BaseActionTypes.SetupMailOtpSuccess, (state, { Payload }) => {
        console.log('### BaseActionTypes.SetupMailOtpSuccess', Payload)
        return {
            ...state,
            user: state.user?.Clone({MFAMethod: Payload.twoFactorMethod}),
        }
    }),
    on(BaseActionTypes.SetupTOTPSuccess, (state) => {
        console.log('### BaseActionTypes.SetupTOTPSuccess')
        return {
            ...state,
            user: state.user?.Clone({MFAMethod: TwoFactorMethodEnum.Totp}),
        }
    }),
    on(BaseActionTypes.RemoveOTPSuccess, (state) => {
        console.log('### BaseActionTypes.RemoveOTPSuccess')
        return {
            ...state,
            user: state.user?.Clone({MFAMethod: TwoFactorMethodEnum.None}),
        }
    }),
    on(UsersActionTypes.RemoveOTPSuccess, (state, { Payload }) => {
        return adapter.setOne(Payload, {
            ...state,
            user: state.user.Id === Payload.Id ? Payload : state.user,
        })
    }),
);
/* ############### */
/* ## SELECTORS ## */
/* ############### */

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectAllUser = selectAll;
export const selectUserEntities = selectEntities;

export const selectUserFetched = (state: UsersState) => state.fetched;
export const selectSettingFetched = (state: UsersState) => state.settingsFetched;
export const selectUserLatestUpdatedAt = (state: UsersState) => state.latestUpdatedAt;
